import { Button, FileInput, Label, Spinner } from 'flowbite-react';
import React, { FormEvent, useEffect } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { sendOfferLetter } from 'src/apis/application';
import { getPolicyForS3NonVideo } from 'src/apis/user';
import { useToast } from 'src/context/ToastProvider';
import { handleS3Upload } from 'src/utils/imageUpload';
import { OfferedApplicationParameter, SendOfferLetterInput } from 'types';

interface Props {
	applicationId: string;
	userName: string;
	positionId: string;
	onSubmit?: (data: SendOfferLetterInput) => void;
	onFinish?: () => void;
	offeredPositionParameters?: OfferedApplicationParameter;
}

const RecruiterCandidateAddOfferLetter = ({
	applicationId,
	userName,
	onSubmit,
	onFinish,
	positionId,
	offeredPositionParameters,
}: Props) => {
	const toast = useToast();
	const queryClient = useQueryClient();
	const [file, setFile] = React.useState<File | null>();
	const [isLoading, setIsLoading] = React.useState(false);
	const [offerLetterInput, setOfferLetterInput] = React.useState<SendOfferLetterInput>({
		positionId,
		applicationId,
		offerLetterLink: '',
		remarks: '',
		offeredPositionParameters,
	});

	const sendOfferLetterFn = useMutation(['sendOfferLetter'], sendOfferLetter, {
		onSuccess() {
			toast({
				title: userName ? `Offer sent to ${userName}` : 'Offer sent',
				type: 'success',
			});
			setOfferLetterInput({
				positionId,
				applicationId,
				remarks: '',
				offerLetterLink: '',
				offeredPositionParameters,
			});
			queryClient.refetchQueries({ queryKey: ['getRecommendationsForPosition'], exact: false });
			setFile(null);
			onFinish && onFinish();
		},
		onError(error: Error) {
			toast({
				title: `Error occured ${error.message}`,
				type: 'error',
			});
		},
	});

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (isLoading) {
			return;
		}
		let imageUrl = '';
		if (file) {
			const fileSizeMB = file.size / (1024 * 1024);
			if (fileSizeMB > 5) {
				toast({
					type: 'error',
					title: 'File size exceeds the maximum limit of 5 MB',
					duration: 10000,
				});
				return;
			}

			setIsLoading(true);

			const policy = await getPolicyForS3NonVideo(file.name, 'offerLetters', file.type, true);
			const uploadedUrl = await handleS3Upload(policy, file);
			setIsLoading(false);
			if (uploadedUrl) imageUrl = uploadedUrl;
		}

		if (onSubmit)
			onSubmit({
				...offerLetterInput,
				offerLetterLink: imageUrl,
			});
		else {
			sendOfferLetterFn.mutate({
				...offerLetterInput,
				offerLetterLink: imageUrl,
			});
		}
	};

	return (
		<form onSubmit={handleSubmit}>
			<div className="relative mt-[16px]  ">
				<div className="mb-[8px]">
					<Label>{'Upload Offer '}</Label>
					{offerLetterInput?.offerLetterLink ? (
						<a
							href={offerLetterInput?.offerLetterLink}
							target="_blank"
							className="text-[#7777E7] underline text-xs ml-1"
						>
							(Preview){' '}
						</a>
					) : (
						<span className="mt-2 text-xs italic text-gray-500 dark:text-gray-400">(Optional)</span>
					)}
				</div>

				<FileInput
					id="hukumu-profile-resume-file-input"
					accept=".pdf,doc,docx"
					disabled={isLoading}
					// value={offerLetterInput?.offerLetterLink}
					helperText="Only txt, pdf, doc files allowed."
					className="w-full text-xs font-normal italic min-h-[42px] [&~p]:italic"
					onChange={event => {
						if (event.target.files) {
							console.log('event.target.files[0]:', event.target.files[0]);
							setFile && setFile(event.target.files[0]);
						}
					}}
				/>
			</div>

			<Button
				type="submit"
				className="w-full mt-[20px] bg-[#7777E7]"
				color="purplePlaced"
				disabled={!file || sendOfferLetterFn.isLoading || isLoading}
			>
				{sendOfferLetterFn.isLoading || isLoading ? <Spinner /> : 'Send Offer'}
			</Button>
		</form>
	);
};

export default RecruiterCandidateAddOfferLetter;
